import { Button, Card, CardContent, Input, Label } from "@exhousing/design-system";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { apiClient } from "@/api/client";

function useInviteLandlord() {
  return useMutation({
    mutationFn: async (input: {
      landlordId: string;
      email: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data, error } = await apiClient.POST("/api/v1/users/invite-landlord", {
        body: {
          landlord_id: input.landlordId,
          email: input.email,
          first_name: input.firstName,
          last_name: input.lastName,
        },
      });
      if (error || !data) {
        const detail = (error as { detail?: string } | undefined)?.detail;
        throw new Error(detail ?? "Could not send the invitation");
      }
      return data;
    },
  });
}

/**
 * Journey 1.1: "Geef portaaltoegang" — invite the landlord into the restricted portal.
 * Re-inviting the same address re-issues the link (the old one dies).
 */
export function PortalAccessButton({
  landlordId,
  defaultEmail,
  defaultFirstName,
  defaultLastName,
}: {
  landlordId: string;
  defaultEmail?: string | null;
  defaultFirstName?: string | null;
  defaultLastName?: string | null;
}) {
  const invite = useInviteLandlord();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [firstName, setFirstName] = useState(defaultFirstName ?? "");
  const [lastName, setLastName] = useState(defaultLastName ?? "");

  const send = () =>
    invite.mutate(
      { landlordId, email, firstName, lastName },
      {
        onSuccess: () => {
          toast.success(`Invitation sent to ${email} — valid for 14 days`);
          setOpen(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <KeyRound className="size-4" />
        Portal access
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-xl font-medium">
                    Invite to the landlord portal
                  </h2>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    They see their own properties, candidates and contracts, and
                    make the final selection. Read-only otherwise.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
                >
                  <X className="size-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ll-first">First name</Label>
                  <Input
                    id="ll-first"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ll-last">Last name</Label>
                  <Input
                    id="ll-last"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ll-email">E-mail</Label>
                <Input
                  id="ll-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={invite.isPending || !email || !firstName || !lastName}
                  onClick={send}
                >
                  {invite.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <KeyRound className="size-4" />
                  )}
                  Send invitation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
